import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import { ReactNode } from 'react';

import { EmptyRow, HeadCell, Scroller } from './styles';

export interface DossierColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
}

interface DossierTableProps<T> {
  columns: DossierColumn[];
  rows: T[];
  getRowId: (row: T) => string;
  renderRow: (row: T) => ReactNode;
  emptyMessage: string;
  footer?: ReactNode;
  /** Class for the row, e.g. `is-current` to highlight the current OS. */
  rowClassName?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  ariaLabel?: string;
}

/**
 * Small v2 table for dossier blocks (contacts, charges, files, related
 * requests): a handful of rows, no sorting, no pagination. The theme paints
 * head cells; this only adds the surface, the empty row and the footer slot.
 */
export function DossierTable<T>({
  columns,
  rows,
  getRowId,
  renderRow,
  emptyMessage,
  footer,
  rowClassName,
  onRowClick,
  ariaLabel,
}: DossierTableProps<T>): JSX.Element {
  return (
    <Scroller>
      <Table size="small" aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <HeadCell
                key={column.key}
                align={column.align ?? 'left'}
                sx={column.width ? { width: column.width } : undefined}
              >
                {column.label}
              </HeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <EmptyRow colSpan={columns.length}>{emptyMessage}</EmptyRow>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={getRowId(row)}
                className={rowClassName?.(row)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {renderRow(row)}
              </TableRow>
            ))
          )}
        </TableBody>
        {footer}
      </Table>
    </Scroller>
  );
}
